import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { createStyles, lighten, makeStyles, Theme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import DeleteIcon from '@material-ui/icons/Delete';
import FilterListIcon from '@material-ui/icons/FilterList';
import { usePedidos } from '../../../../contexts/Pedidos';
import { useHistory } from 'react-router-dom';
import { PaginationTable } from '../../../../themes/components/PaginationTable';
import { ItemResumoPrePedido } from '../../interfaces';
import { TextOnlyTooltip } from '../../../../themes/components/TextOnlyTooltip';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (parseInt(String(b[orderBy])) < parseInt(String(a[orderBy]))) {
    return -1;
  }
  if (parseInt(String(b[orderBy])) > parseInt(String(a[orderBy]))) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
}

interface Data {
  cod_agrupamento: string;
  cod_derivacao: string;
  cod_produto: string;
  preco_unitario: number;
  quantidade: number;
  valor: number;
  quantidade_corte: number;
}

const headCells: HeadCell[] = [
  { id: 'cod_agrupamento', numeric: false, disablePadding: true, label: 'Agrup.' },
  { id: 'cod_produto', numeric: false, disablePadding: false, label: 'Produto' },
  { id: 'cod_derivacao', numeric: false, disablePadding: false, label: 'Derivação' },
  { id: 'preco_unitario', numeric: true, disablePadding: false, label: 'Preço unitário' },
  { id: 'quantidade', numeric: true, disablePadding: false, label: 'Quantidade' },
  { id: 'quantidade_corte', numeric: true, disablePadding: false, label: 'Corte' },
  { id: 'valor', numeric: true, disablePadding: false, label: 'Total' },
];

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  allowSelectitem: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {props.allowSelectitem ?
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{ 'aria-label': 'select all desserts' }}
            />
          </TableCell>
          :
          <></>
        }
        {headCells.map((headCell) => (

          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ paddingLeft: props.allowSelectitem ? "0px" : "1em", whiteSpace: 'nowrap' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    highlight:
      theme.palette.type === 'light'
        ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
        : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
    title: {
      flex: '1 1 100%',
    },
  }),
);

interface EnhancedTableToolbarProps {
  numSelected: number;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </Typography>
      ) : (
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">

        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '97.6%',
      margin: '0px 1em 0px 1em',
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
      border: '1px solid #ddd',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  }),
);

interface tableProps {
  allowSelectitem: boolean,
  listaItens: ItemResumoPrePedido[]
}


export function TabelaListaItensResumoPedido(props: tableProps) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Order>('asc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('cod_agrupamento');
  const [dense, setDense] = React.useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  const emptyRows = perPage - Math.min(perPage, props.listaItens.length - (currentPage - 1) * perPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TextOnlyTooltip title="Densidade da Tabela" placement="top">
          <FormControlLabel
            control={<Switch checked={dense} onChange={handleChangeDense} />}
            label=""
            style={{ marginLeft: '0.3em' }}
          />
        </TextOnlyTooltip>
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="Tabela de Itens do pedido"
            size={dense ? 'small' : 'medium'}
            aria-label="Tabela de Itens do pedido"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={0}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={() => { }}
              onRequestSort={handleRequestSort}
              rowCount={props.listaItens.length}
              allowSelectitem={props.allowSelectitem}
            />
            <TableBody>
              {stableSort(props.listaItens, getComparator(order, orderBy))
                .slice((currentPage - 1) * perPage, (currentPage - 1) * perPage + perPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={row.cod_derivacao + row.cod_produto + row.numero_container}
                    >
                      {props.allowSelectitem ?
                        <TableCell padding="checkbox">
                          <Checkbox
                            inputProps={{ 'aria-labelledby': labelId }}
                            color={'primary'}
                          />
                        </TableCell>
                        :
                        <></>
                      }
                      <TableCell
                        id={labelId}
                        scope="row"
                        padding="none"
                        style={{ paddingLeft: props.allowSelectitem ? "0px" : "1em" }}>
                        {row.cod_agrupamento}
                      </TableCell>
                      <TableCell align="left">{row.cod_produto + " - " + row.desc_produto}</TableCell>
                      <TableCell align="left">{row.cod_derivacao}</TableCell>
                      <TableCell align="right">{row.preco_unitario.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}</TableCell>
                      <TableCell align="right">{row.quantidade.toLocaleString('pt-br', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2 })}</TableCell>
                      <TableCell align="right">{row.quantidade_corte !== 0 ? row.quantidade_corte.toLocaleString('pt-br', { style: 'decimal', minimumFractionDigits: 0, maximumFractionDigits: 2 }) : ""}</TableCell>
                      <TableCell align="right">{row.valor.toLocaleString('pt-br', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationTable
          textSingle="Item"
          textPlural="Itens"
          arrayItens={props.listaItens}
          page={currentPage}
          countPages={Math.ceil(props.listaItens.length / perPage)}
          defaultPage={1}
          handleChange={handleChangePage}
          shape="round"
          styleCLasses={"padding-right"}
          setCurrentPage={setCurrentPage}
          setPerPage={setPerPage}
          optionsPerPage={[5, 10, 50]}
          perPage={perPage}
        />
      </Paper>
    </div>
  );
}